import React, { useEffect, useMemo, useState } from "react";
import { Link2, Users2, DollarSign, Settings as Cog, Plus, Copy, Check, Search, Power, Pause } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../api";

const TABS = [
  { id: "partners", label: "Partners", icon: Users2 },
  { id: "dashboard", label: "Dashboard", icon: DollarSign },
  { id: "payouts", label: "Payouts", icon: Link2 },
  { id: "settings", label: "Settings", icon: Cog },
];

export default function Affiliates() {
  const [tab, setTab] = useState("partners");
  const [totals, setTotals] = useState(null); // shared between Partners + Dashboard
  return (
    <div className="space-y-5" data-testid="admin-affiliates-page">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-medium tracking-tight">Affiliate Program</h1>
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mt-1">
            // partners · revenue-share · payouts
          </p>
        </div>
      </header>

      <nav className="flex border-b border-border" data-testid="affiliates-subnav">
        {TABS.map((t) => (
          <button
            key={t.id}
            data-testid={`affiliate-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2.5 text-sm flex items-center gap-2 border-b-2 transition-colors -mb-px ${
              tab === t.id
                ? "border-foreground text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            <t.icon className="w-3.5 h-3.5" />
            <span>{t.label}</span>
          </button>
        ))}
      </nav>

      <div>
        {tab === "partners" && (
          <PartnersTab totals={totals} setTotals={setTotals} />
        )}
        {tab === "dashboard" && (
          <DashboardTab totals={totals} setTotals={setTotals} />
        )}
        {tab === "payouts" && <PayoutsTab />}
        {tab === "settings" && <SettingsTab />}
      </div>
    </div>
  );
}

// ── KPI cards ───────────────────────────────────────────────────────────

function KpiCard({ label, value, accent = "default", testid }) {
  const cls =
    accent === "yellow"
      ? "border-amber-500/40"
      : accent === "green"
      ? "border-emerald-500/40"
      : "border-border";
  return (
    <div
      data-testid={testid}
      className={`border ${cls} bg-secondary/20 px-4 py-3`}
    >
      <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">
        {label}
      </div>
      <div className="text-2xl font-semibold mt-1 tabular-nums">{value}</div>
    </div>
  );
}

function fmtUsd(v) {
  if (v === null || v === undefined) return "—";
  return `$${Number(v).toFixed(2)}`;
}

function fmtNum(v) {
  if (v === null || v === undefined) return "—";
  return v;
}

// ── Partners tab ────────────────────────────────────────────────────────

function PartnersTab({ totals, setTotals }) {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [requestedOnly, setRequestedOnly] = useState(false);
  const [minBal, setMinBal] = useState(0);
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [inviteShown, setInviteShown] = useState(null);
  const [detailId, setDetailId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (requestedOnly) params.set("requested_payout", "true");
      if (minBal) params.set("min_balance", minBal);
      const { data } = await api.get(`/admin/affiliates?${params.toString()}`);
      setItems(data.items || []);
      setTotals(data.totals || {});
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, requestedOnly]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="affiliates-kpis">
        <KpiCard label="Partners" value={fmtNum(totals?.affiliates)} testid="kpi-partners" />
        <KpiCard label="Sign-ups" value={fmtNum(totals?.registrations)} testid="kpi-signups" />
        <KpiCard label="Earned" value={fmtUsd(totals?.earned_usd)} testid="kpi-earned" />
        <KpiCard label="Paid out" value={fmtUsd(totals?.paid_usd)} testid="kpi-paid" />
        <KpiCard label="Pending" value={fmtUsd(totals?.pending_usd)} accent="yellow" testid="kpi-pending" />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            data-testid="affiliates-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search name or email"
            className="pl-8 pr-3 py-1.5 text-sm bg-background border border-border rounded-none focus:outline-none focus:border-foreground/40 w-64"
          />
        </div>
        <select
          data-testid="affiliates-status-filter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-1.5 text-sm bg-background border border-border focus:outline-none"
        >
          <option value="all">All statuses</option>
          <option value="active">Active</option>
          <option value="invited">Invited</option>
          <option value="disabled">Disabled</option>
        </select>
        <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <input
            type="checkbox"
            data-testid="affiliates-requested-only"
            checked={requestedOnly}
            onChange={(e) => setRequestedOnly(e.target.checked)}
          />
          Requested payout
        </label>
        <input
          type="number"
          data-testid="affiliates-min-balance"
          value={minBal || ""}
          onChange={(e) => setMinBal(Number(e.target.value || 0))}
          placeholder="Min $"
          className="w-24 px-2 py-1.5 text-sm bg-background border border-border focus:outline-none"
        />
        <button
          data-testid="affiliates-apply-filters"
          onClick={load}
          className="px-3 py-1.5 text-sm border border-border hover:border-foreground/40"
        >
          Apply
        </button>

        <div className="ml-auto">
          <button
            data-testid="affiliates-create-btn"
            onClick={() => setCreating(true)}
            className="px-3 py-1.5 text-sm bg-foreground text-background flex items-center gap-1.5 hover:opacity-90"
          >
            <Plus className="w-3.5 h-3.5" />
            New partner
          </button>
        </div>
      </div>

      <div className="border border-border" data-testid="affiliates-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-secondary/30 text-[11px] font-mono uppercase tracking-[0.18em] text-muted-foreground">
              <th className="text-left px-3 py-2">Partner</th>
              <th className="text-right px-3 py-2">%</th>
              <th className="text-right px-3 py-2">Sign-ups</th>
              <th className="text-right px-3 py-2">Earned</th>
              <th className="text-right px-3 py-2">Paid</th>
              <th className="text-right px-3 py-2">Pending</th>
              <th className="text-left px-3 py-2 pl-4">Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan="8" className="px-3 py-6 text-center text-muted-foreground text-xs">
                  Loading…
                </td>
              </tr>
            )}
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan="8" className="px-3 py-6 text-center text-muted-foreground text-xs">
                  No partners yet — invite one with “New partner”.
                </td>
              </tr>
            )}
            {items.map((p) => (
              <PartnerRow
                key={p.id}
                partner={p}
                onChanged={load}
                onShowInvite={(link) => setInviteShown(link)}
                onOpenDetail={() => setDetailId(p.id)}
              />
            ))}
          </tbody>
        </table>
      </div>

      {creating && (
        <CreatePartnerModal
          onClose={() => setCreating(false)}
          onCreated={(p) => {
            setCreating(false);
            load();
            if (p?.invite_link) setInviteShown(p.invite_link);
          }}
        />
      )}
      {inviteShown && (
        <InviteLinkModal link={inviteShown} onClose={() => setInviteShown(null)} />
      )}
      {detailId && (
        <PartnerDetailDrawer
          affiliateId={detailId}
          onClose={() => setDetailId(null)}
          onChanged={load}
        />
      )}
    </div>
  );
}

function PartnerRow({ partner, onChanged, onOpenDetail }) {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [pct, setPct] = useState(partner.commission_pct);

  const toggleStatus = async () => {
    const next = partner.status === "disabled" ? "active" : "disabled";
    try {
      await api.patch(`/admin/affiliates/${partner.id}`, { status: next });
      toast.success(`Partner ${next}`);
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const savePct = async () => {
    try {
      await api.patch(`/admin/affiliates/${partner.id}`, {
        commission_pct: Number(pct),
      });
      toast.success("Commission updated");
      setEditing(false);
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const k = partner.kpis || {};
  return (
    <>
      <tr
        data-testid={`affiliate-row-${partner.id}`}
        className="border-b border-border hover:bg-secondary/20"
      >
        <td className="px-3 py-2.5">
          <button
            type="button"
            onClick={onOpenDetail}
            data-testid={`open-partner-${partner.id}`}
            className="flex flex-col text-left hover:opacity-80 transition-opacity cursor-pointer"
          >
            <span className="font-medium underline-offset-4 hover:underline">
              {partner.name}
            </span>
            <span className="text-[11px] text-muted-foreground font-mono">
              {partner.email}
            </span>
          </button>
        </td>
        <td className="px-3 py-2.5 text-right tabular-nums">
          {editing ? (
            <div className="flex items-center justify-end gap-1">
              <input
                type="number"
                value={pct}
                onChange={(e) => setPct(e.target.value)}
                className="w-16 px-1 py-0.5 bg-background border border-border text-right"
              />
              <button
                onClick={savePct}
                className="text-emerald-400 px-1"
                data-testid={`save-pct-${partner.id}`}
              >
                <Check className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : (
            <button
              data-testid={`edit-pct-${partner.id}`}
              onClick={() => setEditing(true)}
              className="hover:text-foreground"
            >
              {partner.commission_pct}%
            </button>
          )}
        </td>
        <td className="px-3 py-2.5 text-right tabular-nums">{k.registrations ?? 0}</td>
        <td className="px-3 py-2.5 text-right tabular-nums">{fmtUsd(k.earned_usd)}</td>
        <td className="px-3 py-2.5 text-right tabular-nums">{fmtUsd(k.paid_usd)}</td>
        <td className="px-3 py-2.5 text-right tabular-nums">
          <span className={k.pending_usd > 0 ? "text-amber-400" : ""}>
            {fmtUsd(k.pending_usd)}
          </span>
        </td>
        <td className="px-3 py-2.5 pl-4">
          <span
            className={`inline-block px-2 py-0.5 text-[10px] font-mono uppercase tracking-[0.18em] border ${
              partner.status === "active"
                ? "border-emerald-500/40 text-emerald-400"
                : partner.status === "invited"
                ? "border-amber-500/40 text-amber-400"
                : "border-border text-muted-foreground"
            }`}
          >
            {partner.status}
          </span>
        </td>
        <td className="px-3 py-2.5 text-right">
          <div className="flex justify-end gap-1">
            <button
              data-testid={`partner-expand-${partner.id}`}
              onClick={() => setExpanded((v) => !v)}
              className="text-xs text-muted-foreground hover:text-foreground px-2"
            >
              {expanded ? "Hide" : "Pay"}
            </button>
            <button
              data-testid={`partner-toggle-${partner.id}`}
              onClick={toggleStatus}
              title={partner.status === "disabled" ? "Enable" : "Disable"}
              className="text-muted-foreground hover:text-foreground px-2"
            >
              {partner.status === "disabled" ? (
                <Power className="w-3.5 h-3.5" />
              ) : (
                <Pause className="w-3.5 h-3.5" />
              )}
            </button>
          </div>
        </td>
      </tr>
      {expanded && (
        <tr className="border-b border-border bg-secondary/10">
          <td colSpan="8" className="px-3 py-3">
            <PayoutForm partner={partner} onDone={onChanged} />
          </td>
        </tr>
      )}
    </>
  );
}

function PayoutForm({ partner, onDone }) {
  const [amount, setAmount] = useState(partner.kpis?.pending_usd || 0);
  const [method, setMethod] = useState("manual");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!amount || amount <= 0) return toast.error("Amount must be > 0");
    setBusy(true);
    try {
      await api.post(`/admin/affiliates/${partner.id}/payouts`, {
        amount_usd: Number(amount),
        method,
        note,
      });
      toast.success("Payout recorded");
      onDone();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-wrap items-end gap-2 text-xs">
      <div>
        <label className="text-[10px] uppercase font-mono tracking-[0.18em] text-muted-foreground">
          Amount (USD)
        </label>
        <input
          type="number"
          step="0.01"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          className="block w-32 px-2 py-1 bg-background border border-border"
          data-testid={`payout-amount-${partner.id}`}
        />
      </div>
      <div>
        <label className="text-[10px] uppercase font-mono tracking-[0.18em] text-muted-foreground">
          Method
        </label>
        <select
          value={method}
          onChange={(e) => setMethod(e.target.value)}
          className="block w-32 px-2 py-1 bg-background border border-border"
        >
          <option value="manual">Manual</option>
          <option value="card">Card</option>
          <option value="bank">Bank</option>
          <option value="ton_wallet">TON wallet</option>
        </select>
      </div>
      <div className="flex-1 min-w-[200px]">
        <label className="text-[10px] uppercase font-mono tracking-[0.18em] text-muted-foreground">
          Note
        </label>
        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Optional"
          className="block w-full px-2 py-1 bg-background border border-border"
        />
      </div>
      <button
        onClick={submit}
        disabled={busy}
        data-testid={`payout-submit-${partner.id}`}
        className="px-3 py-1.5 bg-foreground text-background hover:opacity-90 disabled:opacity-50"
      >
        Record payout
      </button>
    </div>
  );
}

function CreatePartnerModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    commission_pct: 20,
    display_currency: "USD",
    distribution_channels: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/affiliates", {
        name: form.name,
        email: form.email || null,
        commission_pct: Number(form.commission_pct),
        display_currency: form.display_currency,
        distribution_channels: form.distribution_channels
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
      });
      toast.success("Partner created");
      onCreated(data);
    } catch (er) {
      toast.error(formatApiError(er));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
      data-testid="create-partner-modal"
      onClick={onClose}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="bg-background border border-border max-w-md w-full p-5 space-y-3"
      >
        <h2 className="text-lg font-medium">New partner</h2>
        <Field
          label="Name"
          value={form.name}
          required
          onChange={(v) => setForm({ ...form, name: v })}
          testid="new-partner-name"
        />
        <Field
          label="Email (optional)"
          value={form.email}
          onChange={(v) => setForm({ ...form, email: v })}
          testid="new-partner-email"
        />
        <div className="grid grid-cols-2 gap-3">
          <Field
            label="Commission %"
            type="number"
            value={form.commission_pct}
            onChange={(v) => setForm({ ...form, commission_pct: v })}
            testid="new-partner-pct"
          />
          <div>
            <label className="text-[10px] uppercase font-mono tracking-[0.18em] text-muted-foreground block mb-1">
              Display currency
            </label>
            <select
              value={form.display_currency}
              onChange={(e) => setForm({ ...form, display_currency: e.target.value })}
              className="w-full px-2 py-1.5 bg-background border border-border text-sm"
              data-testid="new-partner-currency"
            >
              <option value="USD">USD</option>
              <option value="EUR">EUR</option>
              <option value="USDT">USDT</option>
              <option value="TON">TON</option>
              <option value="RUB">RUB</option>
            </select>
          </div>
        </div>
        <Field
          label="Channels (comma-separated)"
          value={form.distribution_channels}
          placeholder="YouTube, Telegram"
          onChange={(v) => setForm({ ...form, distribution_channels: v })}
          testid="new-partner-channels"
        />
        <div className="flex gap-2 justify-end pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-sm border border-border"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy}
            data-testid="new-partner-submit"
            className="px-3 py-1.5 text-sm bg-foreground text-background disabled:opacity-50"
          >
            {busy ? "Creating…" : "Create + invite"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, placeholder, testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase font-mono tracking-[0.18em] text-muted-foreground block mb-1">
        {label}
      </label>
      <input
        type={type}
        value={value}
        required={required}
        placeholder={placeholder}
        data-testid={testid}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-2 py-1.5 bg-background border border-border text-sm focus:outline-none focus:border-foreground/40"
      />
    </div>
  );
}

function InviteLinkModal({ link, onClose }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
      data-testid="invite-link-modal"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-background border border-border max-w-md w-full p-5 space-y-3"
      >
        <h2 className="text-lg font-medium">Invite link</h2>
        <p className="text-xs text-muted-foreground">
          Share this one-time link with the partner. They'll set their password
          when they claim it.
        </p>
        <div className="flex items-center gap-2 bg-secondary/30 px-2 py-2 font-mono text-xs break-all">
          <span className="flex-1">{link}</span>
          <button
            onClick={copy}
            data-testid="invite-copy-btn"
            className="text-muted-foreground hover:text-foreground"
          >
            {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
          </button>
        </div>
        <div className="flex justify-end pt-2">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-sm border border-border"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Dashboard tab ───────────────────────────────────────────────────────

function DashboardTab({ totals, setTotals }) {
  useEffect(() => {
    // Only fetch if parent hasn't loaded totals yet (first-paint after
    // landing directly on Dashboard tab without visiting Partners first).
    if (totals === null || totals === undefined) {
      api.get("/admin/affiliates").then(({ data }) => setTotals(data.totals || {}));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="affiliates-dashboard">
        <KpiCard label="Partners" value={fmtNum(totals?.affiliates)} />
        <KpiCard label="Sign-ups" value={fmtNum(totals?.registrations)} />
        <KpiCard label="Earned" value={fmtUsd(totals?.earned_usd)} />
        <KpiCard label="Paid out" value={fmtUsd(totals?.paid_usd)} />
        <KpiCard label="Pending" value={fmtUsd(totals?.pending_usd)} accent="yellow" />
      </div>
      <p className="text-xs text-muted-foreground">
        High-level health of the program. For per-partner breakdown, see{" "}
        <strong>Partners</strong>.
      </p>
    </div>
  );
}

// ── Payouts tab ─────────────────────────────────────────────────────────

function PayoutsTab() {
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");

  const load = async () => {
    const params = new URLSearchParams();
    if (statusFilter) params.set("status", statusFilter);
    const { data } = await api.get(`/admin/affiliates/payouts/all?${params}`);
    setItems(data.items || []);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const markPaid = async (id) => {
    try {
      await api.patch(`/admin/affiliates/payouts/${id}`, { status: "paid" });
      toast.success("Marked paid");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-4" data-testid="affiliates-payouts-tab">
      <div className="flex items-center gap-2">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-1.5 text-sm bg-background border border-border"
          data-testid="payouts-status-filter"
        >
          <option value="">All payouts</option>
          <option value="pending">Pending only</option>
          <option value="paid">Paid only</option>
        </select>
      </div>

      <div className="border border-border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-secondary/30 text-[11px] font-mono uppercase tracking-[0.18em] text-muted-foreground">
              <th className="text-left px-3 py-2">Date</th>
              <th className="text-left px-3 py-2">Partner</th>
              <th className="text-right px-3 py-2">USD</th>
              <th className="text-right px-3 py-2">Native</th>
              <th className="text-left px-3 py-2">Method</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-left px-3 py-2">Note</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr>
                <td colSpan="8" className="px-3 py-6 text-center text-muted-foreground text-xs">
                  No payouts yet.
                </td>
              </tr>
            )}
            {items.map((p) => (
              <tr key={p.id} className="border-b border-border" data-testid={`payout-row-${p.id}`}>
                <td className="px-3 py-2 font-mono text-xs">
                  {(p.paid_at || p.created_at).slice(0, 16).replace("T", " ")}
                </td>
                <td className="px-3 py-2">
                  <div className="flex flex-col">
                    <span>{p.affiliate_name}</span>
                    <span className="text-[11px] text-muted-foreground font-mono">
                      {p.affiliate_email}
                    </span>
                  </div>
                </td>
                <td className="px-3 py-2 text-right tabular-nums">{fmtUsd(p.amount_usd)}</td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {Number(p.amount_native).toFixed(2)} {p.currency}
                </td>
                <td className="px-3 py-2">{p.method}</td>
                <td className="px-3 py-2">
                  <span
                    className={`inline-block px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-[0.18em] border ${
                      p.status === "paid"
                        ? "border-emerald-500/40 text-emerald-400"
                        : "border-amber-500/40 text-amber-400"
                    }`}
                  >
                    {p.status}
                  </span>
                </td>
                <td className="px-3 py-2 text-xs text-muted-foreground">{p.note || ""}</td>
                <td className="px-3 py-2 text-right">
                  {p.status === "pending" && (
                    <button
                      data-testid={`mark-paid-${p.id}`}
                      onClick={() => markPaid(p.id)}
                      className="text-xs text-emerald-400 hover:opacity-80"
                    >
                      Mark paid
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Partner detail drawer ───────────────────────────────────────────────

function PartnerDetailDrawer({ affiliateId, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [section, setSection] = useState("overview");

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/admin/affiliates/${affiliateId}`);
      setData(d);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [affiliateId]);

  // Close on ESC
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const k = data?.kpis || {};

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end"
      data-testid="partner-detail-drawer"
    >
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative w-full max-w-2xl bg-background border-l border-border h-full overflow-y-auto animate-in slide-in-from-right duration-200">
        {loading || !data ? (
          <div className="p-10 text-center text-sm text-muted-foreground">
            Loading partner…
          </div>
        ) : (
          <div className="p-6 space-y-5">
            <header className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h2 className="text-xl font-medium truncate">{data.name}</h2>
                <p className="text-xs font-mono text-muted-foreground truncate">
                  {data.email}
                </p>
                <div className="flex items-center gap-2 mt-2">
                  <span
                    className={`inline-block px-2 py-0.5 text-[10px] font-mono uppercase tracking-[0.18em] border ${
                      data.status === "active"
                        ? "border-emerald-500/40 text-emerald-400"
                        : data.status === "invited"
                        ? "border-amber-500/40 text-amber-400"
                        : "border-border text-muted-foreground"
                    }`}
                  >
                    {data.status}
                  </span>
                  <span className="inline-block px-2 py-0.5 text-[10px] font-mono border border-emerald-500/40 text-emerald-400">
                    {data.commission_pct}% revenue share
                  </span>
                  <span className="text-[10px] font-mono text-muted-foreground">
                    {data.display_currency}
                  </span>
                </div>
              </div>
              <button
                onClick={onClose}
                data-testid="partner-drawer-close"
                className="text-muted-foreground hover:text-foreground text-xl leading-none p-1"
              >
                ×
              </button>
            </header>

            <nav className="flex border-b border-border text-xs">
              {[
                { id: "overview", label: "Overview" },
                { id: "codes", label: `Codes (${data.codes?.length || 0})` },
                { id: "transactions", label: `Transactions (${data.transactions?.length || 0})` },
                { id: "payouts", label: `Payouts (${data.payouts?.length || 0})` },
              ].map((t) => (
                <button
                  key={t.id}
                  onClick={() => setSection(t.id)}
                  data-testid={`drawer-section-${t.id}`}
                  className={`px-3 py-2 border-b-2 -mb-px transition-colors ${
                    section === t.id
                      ? "border-foreground text-foreground"
                      : "border-transparent text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </nav>

            {section === "overview" && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <KpiCard label="Sign-ups" value={fmtNum(k.registrations)} />
                  <KpiCard label="Active payers" value={fmtNum(k.active_payers)} />
                  <KpiCard label="Earned" value={fmtUsd(k.earned_usd)} accent="green" />
                  <KpiCard label="Pending" value={fmtUsd(k.pending_usd)} accent="yellow" />
                </div>
                <DetailRow label="Created" value={data.created_at?.slice(0, 16).replace("T", " ") || "—"} />
                <DetailRow label="Activated" value={data.activated_at?.slice(0, 16).replace("T", " ") || "—"} />
                <DetailRow
                  label="Distribution channels"
                  value={
                    (data.distribution_channels || []).length
                      ? data.distribution_channels.join(", ")
                      : "—"
                  }
                />
                <DetailRow label="Payout details">
                  {Object.keys(data.payout_details || {}).length === 0 ? (
                    <span className="text-muted-foreground">— not provided —</span>
                  ) : (
                    <div className="space-y-1 mt-1">
                      {Object.entries(data.payout_details).map(([k2, v]) => (
                        <div key={k2} className="text-xs font-mono">
                          <span className="text-muted-foreground">{k2}:</span>{" "}
                          {v}
                        </div>
                      ))}
                    </div>
                  )}
                </DetailRow>
              </div>
            )}

            {section === "codes" && (
              <DetailSection
                empty="No codes yet."
                rows={data.codes || []}
                render={(c) => (
                  <div
                    key={c.id}
                    className="border border-border bg-secondary/20 px-3 py-2.5 flex items-center justify-between text-sm"
                    data-testid={`drawer-code-${c.code}`}
                  >
                    <div>
                      <div className="font-mono text-emerald-400">{c.code}</div>
                      <div className="text-[11px] text-muted-foreground">
                        {c.label || "no label"} ·{" "}
                        {c.created_at?.slice(0, 10)}
                      </div>
                    </div>
                    <span
                      className={`text-[10px] font-mono uppercase tracking-[0.18em] px-2 py-0.5 border ${
                        c.status === "active"
                          ? "border-emerald-500/40 text-emerald-400"
                          : "border-border text-muted-foreground"
                      }`}
                    >
                      {c.status}
                    </span>
                  </div>
                )}
              />
            )}

            {section === "transactions" && (
              <DetailSection
                empty="No transactions yet."
                rows={data.transactions || []}
                render={(t) => (
                  <div
                    key={t.id}
                    className="border border-border bg-secondary/20 px-3 py-2 grid grid-cols-12 text-xs items-center gap-2"
                    data-testid={`drawer-txn-${t.id}`}
                  >
                    <div className="col-span-3 font-mono">
                      {t.created_at?.slice(0, 16).replace("T", " ")}
                    </div>
                    <div className="col-span-3 font-mono">{t.user_label}</div>
                    <div className="col-span-2 text-right tabular-nums">
                      {t.payment_currency === "XTR"
                        ? `${Math.round(t.payment_amount_native)} ⭐`
                        : `${Number(t.payment_amount_native).toFixed(2)} ${t.payment_currency}`}
                    </div>
                    <div className="col-span-2 text-right tabular-nums text-muted-foreground">
                      {fmtUsd(t.payment_amount_usd)}
                    </div>
                    <div className="col-span-2 text-right tabular-nums text-emerald-400">
                      {fmtUsd(t.commission_amount_usd)}
                    </div>
                  </div>
                )}
              />
            )}

            {section === "payouts" && (
              <DetailSection
                empty="No payouts yet."
                rows={data.payouts || []}
                render={(p) => (
                  <div
                    key={p.id}
                    className="border border-border bg-secondary/20 px-3 py-2 grid grid-cols-12 text-xs items-center gap-2"
                    data-testid={`drawer-payout-${p.id}`}
                  >
                    <div className="col-span-3 font-mono">
                      {(p.paid_at || p.created_at)?.slice(0, 16).replace("T", " ")}
                    </div>
                    <div className="col-span-2 text-right tabular-nums">
                      {fmtUsd(p.amount_usd)}
                    </div>
                    <div className="col-span-3">{p.method}</div>
                    <div className="col-span-2">
                      <span
                        className={`inline-block px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-[0.18em] border ${
                          p.status === "paid"
                            ? "border-emerald-500/40 text-emerald-400"
                            : "border-amber-500/40 text-amber-400"
                        }`}
                      >
                        {p.status}
                      </span>
                    </div>
                    <div className="col-span-2 text-muted-foreground truncate">
                      {p.note || ""}
                    </div>
                  </div>
                )}
              />
            )}

            <div className="pt-4 border-t border-border flex justify-end">
              <button
                onClick={onClose}
                className="px-3 py-1.5 text-sm border border-border hover:border-foreground/40"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function DetailRow({ label, value, children }) {
  return (
    <div className="border-b border-border pb-3">
      <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground mb-1">
        {label}
      </div>
      {children !== undefined ? children : (
        <div className="text-sm">{value}</div>
      )}
    </div>
  );
}

function DetailSection({ rows, render, empty }) {
  if (!rows.length) {
    return (
      <div className="text-center text-xs text-muted-foreground py-8">
        {empty}
      </div>
    );
  }
  return <div className="space-y-2">{rows.map(render)}</div>;
}

// ── Settings tab ────────────────────────────────────────────────────────

const PROGRAM_KEYS = [
  "AFFILIATE_ENABLED",
  "AFFILIATE_DEFAULT_COMMISSION_PCT",
  "AFFILIATE_ATTRIBUTION_DAYS",
  "AFFILIATE_MIN_PAYOUT_USD",
  "AFFILIATE_STARS_TO_USD_RATE",
  "AFFILIATE_TON_TO_USD_RATE",
  "AFFILIATE_USDT_TO_USD_RATE",
  "AFFILIATE_USD_TO_RUB_RATE",
];

const SETTING_LABELS = {
  AFFILIATE_ENABLED: "Program enabled",
  AFFILIATE_DEFAULT_COMMISSION_PCT: "Default commission %",
  AFFILIATE_ATTRIBUTION_DAYS: "Attribution period (days, 0=lifetime)",
  AFFILIATE_MIN_PAYOUT_USD: "Min payout threshold (USD)",
  AFFILIATE_STARS_TO_USD_RATE: "1 ⭐ → USD",
  AFFILIATE_TON_TO_USD_RATE: "1 TON → USD",
  AFFILIATE_USDT_TO_USD_RATE: "1 USDT → USD",
  AFFILIATE_USD_TO_RUB_RATE: "1 USD → RUB",
};

function SettingsTab() {
  const [settings, setSettings] = useState([]);
  const [drafts, setDrafts] = useState({});

  const load = async () => {
    const { data } = await api.get("/admin/settings");
    setSettings(data);
  };
  useEffect(() => { load(); }, []);

  const myKeys = useMemo(() => settings.filter((s) => PROGRAM_KEYS.includes(s.key)), [settings]);

  const save = async (key) => {
    const value = drafts[key];
    if (value === undefined) return;
    try {
      await api.put(`/admin/settings/${key}`, { value });
      toast.success(`${key} saved`);
      setDrafts((d) => {
        const n = { ...d }; delete n[key]; return n;
      });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-3 max-w-2xl" data-testid="affiliates-settings-tab">
      <p className="text-xs text-muted-foreground">
        Tunable parameters for the affiliate program. Workers pick up changes within ~30s.
      </p>
      {myKeys.map((s) => (
        <div
          key={s.key}
          className="border border-border bg-secondary/10 px-3 py-2.5 flex items-center gap-3"
          data-testid={`setting-${s.key}`}
        >
          <div className="flex-1">
            <div className="text-sm font-medium">{SETTING_LABELS[s.key] || s.key}</div>
            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground">
              {s.key}
            </div>
          </div>
          <input
            value={drafts[s.key] !== undefined ? drafts[s.key] : s.effective_value || ""}
            onChange={(e) => setDrafts({ ...drafts, [s.key]: e.target.value })}
            className="w-32 px-2 py-1 bg-background border border-border text-sm"
          />
          <button
            onClick={() => save(s.key)}
            disabled={drafts[s.key] === undefined}
            className="px-3 py-1.5 text-sm bg-foreground text-background disabled:opacity-30"
            data-testid={`save-setting-${s.key}`}
          >
            Save
          </button>
        </div>
      ))}
    </div>
  );
}
